import React, { useEffect, useState } from 'react';
import { formatEmailAttachmentSize, type EmailComposeAttachment } from '../../email/composeAttachments';
import { isImageContentType } from '../../email/attachmentUtils';
import { Icon } from '../Icon/Icon';

export interface EmailAttachmentPickerInputProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function EmailAttachmentPickerInput({ inputRef, onFilesSelected }: EmailAttachmentPickerInputProps) {
  return (
    <input
      ref={inputRef}
      type="file"
      multiple
      hidden
      aria-hidden
      tabIndex={-1}
      onChange={onFilesSelected}
    />
  );
}

export interface EmailAttachmentListProps {
  attachments: EmailComposeAttachment[];
  onRemove: (id: string) => void;
}

export function EmailAttachmentList({ attachments, onRemove }: EmailAttachmentListProps) {
  if (attachments.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '10px 15px',
        borderTop: '1px solid var(--cream-2)',
        background: 'var(--cream-0)',
      }}
    >
      {attachments.map((attachment) => (
        <ComposeAttachmentChip key={attachment.id} attachment={attachment} onRemove={onRemove} />
      ))}
    </div>
  );
}

function ComposeAttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: EmailComposeAttachment;
  onRemove: (id: string) => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = isImageContentType(attachment.file.type);

  useEffect(() => {
    if (!isImage) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachment.file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [attachment.file, isImage]);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: '100%',
        padding: '5px 8px 5px 10px',
        borderRadius: 'var(--r-sm)',
        border: '1px solid var(--cream-2)',
        background: 'var(--cream-1)',
        color: 'var(--ink-1)',
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          style={{
            width: 28,
            height: 28,
            borderRadius: 4,
            objectFit: 'cover',
            flexShrink: 0,
            border: '1px solid var(--cream-2)',
          }}
        />
      ) : (
        <Icon kind="attach" size={13} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
      )}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {attachment.file.name}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-4)', flexShrink: 0 }}>
        {formatEmailAttachmentSize(attachment.file.size)}
      </span>
      <button
        type="button"
        aria-label={`Remove ${attachment.file.name}`}
        onClick={() => onRemove(attachment.id)}
        style={{
          border: 0,
          background: 'transparent',
          color: 'var(--ink-3)',
          cursor: 'pointer',
          display: 'inline-flex',
          flexShrink: 0,
        }}
      >
        <Icon kind="x" size={11} />
      </button>
    </span>
  );
}
